import type { Doc, Id } from "../_generated/dataModel";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Parse a YYYY-MM-DD date key into a UTC-midnight timestamp.
 * Returns null for missing or malformed keys.
 */
export function parseDateKey(dateKey: string | undefined | null): number | null {
  if (!dateKey) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const ts = Date.UTC(year, month - 1, day);
  if (!Number.isFinite(ts)) return null;
  // Reject impossible calendar dates that Date.UTC would otherwise normalize forward
  // (e.g. 2026-02-31 -> 2026-03-03).
  const parsed = new Date(ts);
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }
  return ts;
}

function itemBasisValue(tag: Doc<"fieldPilotTags">, item: Doc<"fieldPilotItems">) {
  return tag.progressMode === "measurement_value" ? item.quantityValue ?? 0 : 1;
}

function clampFraction(value: number) {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

export interface FieldPilotProgressCheckpoint {
  dateKey: string;
  /** Cumulative planned progress toward final completion at this date. */
  fraction: number;
}

export type FieldPilotProgressMethod = "completion_statuses" | "milestones";

/**
 * Evaluate the planned-progress curve at `now` on the UTC-encoded calendar axis.
 * Convert a viewer's clock with scheduleCalendarTime before evaluating local schedules.
 */
export function expectedFractionAt(
  checkpoints: FieldPilotProgressCheckpoint[],
  now: number
): number {
  const parsed = checkpoints
    .map((checkpoint) => ({
      ms: parseDateKey(checkpoint.dateKey),
      fraction: clampFraction(checkpoint.fraction),
    }))
    .filter((checkpoint): checkpoint is { ms: number; fraction: number } => checkpoint.ms !== null)
    .sort((a, b) => a.ms - b.ms || a.fraction - b.fraction);
  if (parsed.length === 0) return 0;

  // Collapse same-day checkpoints to the highest cumulative target for that date.
  const normalized: { ms: number; fraction: number }[] = [];
  for (const checkpoint of parsed) {
    const previous = normalized[normalized.length - 1];
    if (previous?.ms === checkpoint.ms) {
      previous.fraction = Math.max(previous.fraction, checkpoint.fraction);
    } else {
      normalized.push({ ...checkpoint });
    }
  }

  if (now < normalized[0].ms) return 0;
  for (let index = 1; index < normalized.length; index += 1) {
    const previous = normalized[index - 1];
    const current = normalized[index];
    if (now > current.ms) continue;
    const spanMs = current.ms - previous.ms;
    if (spanMs <= 0) return current.fraction;
    const elapsed = clampFraction((now - previous.ms) / spanMs);
    return clampFraction(
      previous.fraction + elapsed * (current.fraction - previous.fraction)
    );
  }
  return normalized[normalized.length - 1].fraction;
}

function linearProgressCheckpoints(
  startDateKey: string | null,
  finishDateKey: string | null
): FieldPilotProgressCheckpoint[] {
  if (!startDateKey || !finishDateKey) return [];
  return [
    { dateKey: startDateKey, fraction: 0 },
    { dateKey: finishDateKey, fraction: 1 },
  ];
}

export interface FieldPilotTagScheduleMetrics {
  startDateKey: string | null;
  finishDateKey: string | null;
  /** Calendar duration in days between start and finish (null when either is unset). */
  durationDays: number | null;
  /** Sum of item basis values across all of the tag's items. */
  total: number;
  /** Sum of item basis values in completion statuses. */
  completedValue: number;
  /** Earned progress toward final completion, clamped to [0, 1]. */
  actualFraction: number;
  /** Planned progress toward final completion as of `now`, clamped to [0, 1]. */
  expectedFraction: number;
  /** How earned progress is calculated for this row. */
  progressMethod: FieldPilotProgressMethod;
  /** Piecewise-linear schedule used to refresh planned progress as the date advances. */
  plannedCheckpoints: FieldPilotProgressCheckpoint[];
  /** Projected finish timestamp from the current rate of progress (null when not projectable). */
  projectedFinishMs: number | null;
  /** Projected finish minus planned finish, in days. Negative = ahead, positive = behind. */
  finishVarianceDays: number | null;
  /** True when actual progress trails the timeline expectation. */
  isBehind: boolean;
  /** True when the tag has both a start and finish date configured. */
  hasSchedule: boolean;
}

/**
 * Compute schedule + progress metrics for one tag.
 *
 * For a task without dated milestones, earned progress counts items whose status is a completion
 * status and planned progress is linear from start to finish. Sub-task milestone schedules replace
 * those fractions in {@link computeSubTaskMetrics} so both numbers use the same milestone scale.
 */
export function computeTagScheduleMetrics(args: {
  tag: Doc<"fieldPilotTags">;
  items: Doc<"fieldPilotItems">[];
  completionStatusIds: Set<string>;
  now: number;
}): FieldPilotTagScheduleMetrics {
  const { tag, items, completionStatusIds, now } = args;

  const total = items.reduce((sum, item) => sum + itemBasisValue(tag, item), 0);
  const completedValue = items
    .filter((item) => completionStatusIds.has(String(item.statusId)))
    .reduce((sum, item) => sum + itemBasisValue(tag, item), 0);
  const actualFraction = total > 0 ? clampFraction(completedValue / total) : 0;

  const startMs = parseDateKey(tag.startDateKey);
  const finishMs = parseDateKey(tag.finishDateKey);
  const hasSchedule = startMs !== null && finishMs !== null && finishMs >= startMs;

  let durationDays: number | null = null;
  const plannedCheckpoints = linearProgressCheckpoints(
    tag.startDateKey ?? null,
    tag.finishDateKey ?? null
  );
  let expectedFraction = 0;
  let projectedFinishMs: number | null = null;
  let finishVarianceDays: number | null = null;

  if (hasSchedule && startMs !== null && finishMs !== null) {
    const spanMs = finishMs - startMs;
    durationDays = Math.round(spanMs / MS_PER_DAY);

    expectedFraction = expectedFractionAt(plannedCheckpoints, now);

    const elapsedMs = now - startMs;
    if (actualFraction >= 1) {
      // Complete: treat the finish as met (no projected slip).
      projectedFinishMs = finishMs;
    } else if (elapsedMs > 0 && actualFraction > 0) {
      // Extrapolate total duration from the current rate of progress.
      const projectedSpanMs = elapsedMs / actualFraction;
      projectedFinishMs = startMs + projectedSpanMs;
    } else {
      projectedFinishMs = null;
    }

    if (projectedFinishMs !== null) {
      finishVarianceDays = Math.round((projectedFinishMs - finishMs) / MS_PER_DAY);
    }
  }

  const isBehind = hasSchedule && actualFraction + 1e-9 < expectedFraction;

  return {
    startDateKey: tag.startDateKey ?? null,
    finishDateKey: tag.finishDateKey ?? null,
    durationDays,
    total,
    completedValue,
    actualFraction,
    expectedFraction,
    progressMethod: "completion_statuses",
    plannedCheckpoints,
    projectedFinishMs,
    finishVarianceDays,
    isBehind,
    hasSchedule,
  };
}

/**
 * Resolve the set of status ids (as strings) that count toward completion for a job.
 *
 * When any status carries an explicit `isCompletionStatus` flag, those flags are
 * authoritative — including the case where the user has cleared every one (empty set).
 * Only fully-legacy jobs (no status has the field set) fall back to a status literally
 * named "Installed".
 */
export function resolveCompletionStatusIds(
  statuses: Doc<"fieldPilotStatuses">[]
): Set<string> {
  const hasExplicitFlag = statuses.some(
    (status) => status.isCompletionStatus !== undefined
  );
  if (hasExplicitFlag) {
    return new Set(
      statuses
        .filter((status) => status.isCompletionStatus === true)
        .map((status) => String(status._id))
    );
  }
  const installed = statuses.filter((status) => status.name === "Installed");
  return new Set(installed.map((status) => String(status._id)));
}

export type FieldPilotScheduleTagId = Id<"fieldPilotTags">;

/**
 * Render a UTC-midnight timestamp back to a YYYY-MM-DD date key.
 * Inverse of {@link parseDateKey} for values produced by it.
 */
export function msToDateKey(ms: number): string {
  const d = new Date(ms);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Build the ordered-pipeline rank for a job's statuses.
 *
 * Pass the *active* (non-archived) statuses so the resulting ranks are contiguous.
 * `displayOrder` ascending IS the pipeline: rank 0 is the first stage. A higher rank
 * means "further along" the install pipeline (e.g. Not Installed → Installed → Inspected).
 */
export function buildStatusRank(
  statuses: Doc<"fieldPilotStatuses">[]
): Map<string, number> {
  const rank = new Map<string, number>();
  [...statuses]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .forEach((status, index) => {
      rank.set(String(status._id), index);
    });
  return rank;
}

/** Pipeline rank of an item's current status; -1 when the status is missing/archived. */
function itemStatusRank(
  item: Doc<"fieldPilotItems">,
  statusRank: Map<string, number>
): number {
  const rank = statusRank.get(String(item.statusId));
  return rank === undefined ? -1 : rank;
}

export interface FieldPilotMilestoneMetrics {
  statusId: Id<"fieldPilotStatuses">;
  dueDateKey: string;
  /** Pipeline rank of this milestone's status (-1 when the status is missing). */
  rank: number;
  /** True when every member item has reached at least this milestone's rank (slowest item gates it). */
  reached: boolean;
  /** Fraction of the configured progress basis at or past this milestone's rank, in [0, 1]. */
  fraction: number;
}

export interface FieldPilotSubTaskMetrics extends FieldPilotTagScheduleMetrics {
  subTaskId: Id<"fieldPilotSubTasks">;
  /** Per-milestone progress, ordered by pipeline rank ascending. */
  milestones: FieldPilotMilestoneMetrics[];
  /** Lowest-rank milestone not yet reached (the next target); null when all are reached. */
  nextMilestone: FieldPilotMilestoneMetrics | null;
}

/**
 * Derive a sub-task's planned start/finish from its milestone due dates (min/max).
 * Returns date keys (not timestamps) so callers can cache them on the sub-task row.
 */
export function deriveSubTaskDatesFromMilestones(
  milestones: { dueDateKey: string }[]
): { startDateKey: string | undefined; finishDateKey: string | undefined } {
  const sorted = milestones
    .map((m) => ({ key: m.dueDateKey, ms: parseDateKey(m.dueDateKey) }))
    .filter((m): m is { key: string; ms: number } => m.ms !== null)
    .sort((a, b) => a.ms - b.ms);
  if (sorted.length === 0) {
    return { startDateKey: undefined, finishDateKey: undefined };
  }
  return {
    startDateKey: sorted[0].key,
    finishDateKey: sorted[sorted.length - 1].key,
  };
}

function withProgressCurve(
  base: FieldPilotTagScheduleMetrics,
  args: {
    actualFraction: number;
    plannedCheckpoints: FieldPilotProgressCheckpoint[];
    progressMethod: FieldPilotProgressMethod;
    now: number;
  }
): FieldPilotTagScheduleMetrics {
  const actualFraction = clampFraction(args.actualFraction);
  const expectedFraction = base.hasSchedule
    ? expectedFractionAt(args.plannedCheckpoints, args.now)
    : 0;
  const startMs = parseDateKey(base.startDateKey);
  const finishMs = parseDateKey(base.finishDateKey);
  let projectedFinishMs: number | null = null;
  let finishVarianceDays: number | null = null;
  if (base.hasSchedule && startMs !== null && finishMs !== null) {
    const elapsedMs = args.now - startMs;
    if (actualFraction >= 1) {
      projectedFinishMs = finishMs;
    } else if (elapsedMs > 0 && actualFraction > 0) {
      projectedFinishMs = startMs + elapsedMs / actualFraction;
    }
    if (projectedFinishMs !== null) {
      finishVarianceDays = Math.round((projectedFinishMs - finishMs) / MS_PER_DAY);
    }
  }
  return {
    ...base,
    actualFraction,
    expectedFraction,
    progressMethod: args.progressMethod,
    plannedCheckpoints: args.plannedCheckpoints,
    projectedFinishMs,
    finishVarianceDays,
    isBehind: base.hasSchedule && actualFraction + 1e-9 < expectedFraction,
  };
}

export interface FieldPilotScheduleProgressComponent {
  weight: number;
  actualFraction: number;
  progressMethod: FieldPilotProgressMethod;
  plannedCheckpoints: FieldPilotProgressCheckpoint[];
}

/**
 * Combine sub-task progress curves into a parent task using the task's configured count/quantity
 * basis. The union of checkpoint dates preserves every bend in the component milestone curves.
 */
export function rollupScheduleProgress(args: {
  base: FieldPilotTagScheduleMetrics;
  components: FieldPilotScheduleProgressComponent[];
  now: number;
}): FieldPilotTagScheduleMetrics {
  const components = args.components.filter(
    (component) => Number.isFinite(component.weight) && component.weight > 0
  );
  const totalWeight = components.reduce((sum, component) => sum + component.weight, 0);
  if (totalWeight <= 0) return args.base;

  const actualFraction = components.reduce(
    (sum, component) => sum + component.weight * clampFraction(component.actualFraction),
    0
  ) / totalWeight;
  // Sub-tasks with no dated milestones still contribute weight; fall back to the parent's
  // linear start/finish curve so they don't permanently cap expected progress below 100%.
  const componentCurves = components.map((component) =>
    component.plannedCheckpoints.length > 0
      ? component.plannedCheckpoints
      : args.base.plannedCheckpoints
  );
  const dateKeys = Array.from(
    new Set(componentCurves.flatMap((checkpoints) => checkpoints.map((c) => c.dateKey)))
  ).sort();
  const plannedCheckpoints = dateKeys.map((dateKey) => {
    const at = parseDateKey(dateKey) ?? args.now;
    const fraction = components.reduce(
      (sum, component, index) =>
        sum + component.weight * expectedFractionAt(componentCurves[index]!, at),
      0
    ) / totalWeight;
    return { dateKey, fraction };
  });
  return withProgressCurve(args.base, {
    actualFraction,
    plannedCheckpoints:
      plannedCheckpoints.length > 0 ? plannedCheckpoints : args.base.plannedCheckpoints,
    progressMethod: components.some((component) => component.progressMethod === "milestones")
      ? "milestones"
      : "completion_statuses",
    now: args.now,
  });
}

export interface FieldPilotSubTaskProgressComponent {
  subTaskId: Id<"fieldPilotSubTasks">;
  total: number;
  actualFraction: number;
  progressMethod: FieldPilotProgressMethod;
  plannedCheckpoints: FieldPilotProgressCheckpoint[];
}

/**
 * The items a tag never grouped into a sub-task.
 *
 * Shared by the rollup below and by the payload builders that surface the bucket as its own row,
 * so what a reader sees is exactly what the parent's percentage was computed from (CON-405).
 */
export function selectUngroupedItems(
  items: Doc<"fieldPilotItems">[],
  subTaskIds: Set<string>
): Doc<"fieldPilotItems">[] {
  return items.filter((item) => !item.subTaskId || !subTaskIds.has(String(item.subTaskId)));
}

/** Parent schedule metrics with milestone-aware sub-task progress plus any ungrouped items. */
export function computeRolledUpTagScheduleMetrics(args: {
  tag: Doc<"fieldPilotTags">;
  items: Doc<"fieldPilotItems">[];
  subTasks: FieldPilotSubTaskProgressComponent[];
  completionStatusIds: Set<string>;
  now: number;
}): FieldPilotTagScheduleMetrics {
  const base = computeTagScheduleMetrics({
    tag: args.tag,
    items: args.items,
    completionStatusIds: args.completionStatusIds,
    now: args.now,
  });
  if (args.subTasks.length === 0) return base;

  const subTaskIds = new Set(args.subTasks.map((subTask) => String(subTask.subTaskId)));
  const ungroupedItems = selectUngroupedItems(args.items, subTaskIds);
  const components: FieldPilotScheduleProgressComponent[] = args.subTasks.map((subTask) => ({
    weight: subTask.total,
    actualFraction: subTask.actualFraction,
    progressMethod: subTask.progressMethod,
    plannedCheckpoints: subTask.plannedCheckpoints,
  }));
  if (ungroupedItems.length > 0) {
    const ungrouped = computeTagScheduleMetrics({
      tag: args.tag,
      items: ungroupedItems,
      completionStatusIds: args.completionStatusIds,
      now: args.now,
    });
    components.push({
      weight: ungrouped.total,
      actualFraction: ungrouped.actualFraction,
      progressMethod: ungrouped.progressMethod,
      plannedCheckpoints: ungrouped.plannedCheckpoints,
    });
  }
  return rollupScheduleProgress({ base, components, now: args.now });
}

/** Resolve parent dates and milestone-aware progress together so all payloads stay identical. */
export function computeTagScheduleRollup(args: {
  tag: Doc<"fieldPilotTags">;
  items: Doc<"fieldPilotItems">[];
  subTasks: Array<
    FieldPilotSubTaskProgressComponent & {
      startDateKey: string | null;
      finishDateKey: string | null;
    }
  >;
  completionStatusIds: Set<string>;
  now: number;
}): { effectiveTag: Doc<"fieldPilotTags">; metrics: FieldPilotTagScheduleMetrics } {
  const rolledDates = rollupTagDatesFromSubTasks(args.tag, args.subTasks);
  const effectiveTag: Doc<"fieldPilotTags"> = {
    ...args.tag,
    startDateKey: rolledDates.startDateKey ?? undefined,
    finishDateKey: rolledDates.finishDateKey ?? undefined,
  };
  return {
    effectiveTag,
    metrics: computeRolledUpTagScheduleMetrics({
      tag: effectiveTag,
      items: args.items,
      subTasks: args.subTasks,
      completionStatusIds: args.completionStatusIds,
      now: args.now,
    }),
  };
}

/**
 * Compute schedule + milestone metrics for one sub-task.
 *
 * Schedule math reuses {@link computeTagScheduleMetrics} on a synthetic tag that inherits the
 * parent tag's progress mode (for the item basis) but takes its start/finish from the sub-task's
 * milestone due dates. Each milestone is "reached" only when the slowest member item has reached
 * its rank — a true Gantt rollup. `nextMilestone` is the lowest-rank unreached milestone.
 */
export function computeSubTaskMetrics(args: {
  subTask: Doc<"fieldPilotSubTasks">;
  parentTag: Doc<"fieldPilotTags">;
  milestones: Doc<"fieldPilotMilestones">[];
  items: Doc<"fieldPilotItems">[];
  statusRank: Map<string, number>;
  completionStatusIds: Set<string>;
  now: number;
}): FieldPilotSubTaskMetrics {
  const { subTask, parentTag, milestones, items, statusRank, completionStatusIds, now } = args;

  // Ignore milestones whose status is archived/missing (rank < 0): they must not extend the
  // finish, drive the date rollup, or show up as the next milestone.
  const activeMilestones = milestones.filter((m) => statusRank.has(String(m.statusId)));

  const ms = deriveSubTaskDatesFromMilestones(activeMilestones);
  // START: the sub-task's explicit override wins; otherwise it INHERITS the parent task's start
  // (work begins before the first milestone); finally falls back to the earliest milestone.
  const startDateKey = subTask.startDateKey ?? parentTag.startDateKey ?? ms.startDateKey;
  // FINISH: the final (latest) milestone date; falls back to any cached finish.
  const finishDateKey = ms.finishDateKey ?? subTask.finishDateKey;
  const syntheticTag: Doc<"fieldPilotTags"> = {
    ...parentTag,
    startDateKey,
    finishDateKey,
  };
  const base = computeTagScheduleMetrics({
    tag: syntheticTag,
    items,
    completionStatusIds,
    now,
  });

  const totalCount = items.length;
  const milestoneMetrics: FieldPilotMilestoneMetrics[] = activeMilestones
    .map((milestone) => ({
      milestone,
      rank: statusRank.get(String(milestone.statusId)) ?? -1,
    }))
    .sort((a, b) => a.rank - b.rank)
    .map(({ milestone, rank }) => {
      const atOrPastItems = items.filter(
        (item) => itemStatusRank(item, statusRank) >= rank
      );
      const atOrPastValue = atOrPastItems.reduce(
        (sum, item) => sum + itemBasisValue(parentTag, item),
        0
      );
      return {
        statusId: milestone.statusId,
        dueDateKey: milestone.dueDateKey,
        rank,
        reached: totalCount > 0 && atOrPastItems.length === totalCount,
        fraction: base.total > 0 ? clampFraction(atOrPastValue / base.total) : 0,
      };
    });

  // Rank 0 is the starting state (for example, "Not Installed"), not earned work. Legacy
  // rank-0 milestone rows remain visible, but only milestones after that starting state divide
  // the 0..100 earned-progress scale.
  const progressMilestones = milestoneMetrics.filter((milestone) => milestone.rank > 0);
  let scheduleMetrics = base;
  if (progressMilestones.length > 0) {
    const share = 1 / progressMilestones.length;
    const plannedCheckpoints: FieldPilotProgressCheckpoint[] = base.startDateKey
      ? [
          { dateKey: base.startDateKey, fraction: 0 },
          ...progressMilestones.map((milestone, index) => ({
            dateKey: milestone.dueDateKey,
            fraction: (index + 1) * share,
          })),
        ]
      : [];
    const actualFraction = progressMilestones.reduce(
      (sum, milestone) => sum + milestone.fraction * share,
      0
    );
    scheduleMetrics = withProgressCurve(base, {
      actualFraction,
      plannedCheckpoints,
      progressMethod: "milestones",
      now,
    });
  }

  return {
    ...scheduleMetrics,
    subTaskId: subTask._id,
    milestones: milestoneMetrics,
    nextMilestone: milestoneMetrics.find((m) => !m.reached) ?? null,
  };
}

export interface FieldPilotSubTaskStatusLineEntry {
  statusId: Id<"fieldPilotStatuses">;
  /** Pipeline rank of this status (ascending = further along). */
  rank: number;
  /** Milestone due date for this status on the sub-task, or null when none is set. */
  dueDateKey: string | null;
  /** True when every member item has reached at least this status's rank. */
  reached: boolean;
  /** Number of member items at or past this status's rank. */
  atOrPastCount: number;
  /** Total member items in the sub-task. */
  memberCount: number;
}

/**
 * Build the full per-status line for a sub-task drill-down: ONE entry per active status (in
 * pipeline order), merged with the sub-task's milestone due dates. Unlike
 * {@link computeSubTaskMetrics}'s `milestones` (only statuses with a due date), this returns
 * every status so the UI can render the complete pipeline with a date picker on each row.
 */
export function computeSubTaskStatusLine(args: {
  items: Doc<"fieldPilotItems">[];
  sortedStatuses: Doc<"fieldPilotStatuses">[];
  statusRank: Map<string, number>;
  milestones: Doc<"fieldPilotMilestones">[];
}): FieldPilotSubTaskStatusLineEntry[] {
  const { items, sortedStatuses, statusRank, milestones } = args;
  const memberCount = items.length;
  const dueByStatusId = new Map(
    milestones.map((m) => [String(m.statusId), m.dueDateKey] as const)
  );
  return sortedStatuses.map((status) => {
    const rank = statusRank.get(String(status._id)) ?? -1;
    const atOrPastCount = items.filter(
      (item) => itemStatusRank(item, statusRank) >= rank
    ).length;
    return {
      statusId: status._id,
      rank,
      dueDateKey: dueByStatusId.get(String(status._id)) ?? null,
      reached: memberCount > 0 && atOrPastCount === memberCount,
      atOrPastCount,
      memberCount,
    };
  });
}

/**
 * The sub-task's current pipeline stage = the highest-rank status every member item has reached
 * (the slowest member gates it, true Gantt rollup). Returns null when there are no members or
 * none is fully reached.
 */
export function currentStatusFromStatusLine(
  statusLine: FieldPilotSubTaskStatusLineEntry[]
): Id<"fieldPilotStatuses"> | null {
  let current: FieldPilotSubTaskStatusLineEntry | null = null;
  for (const entry of statusLine) {
    if (entry.reached && (current === null || entry.rank > current.rank)) {
      current = entry;
    }
  }
  return current?.statusId ?? null;
}

/**
 * Roll a parent tag's planned dates up from its sub-tasks.
 *
 * START is owner-driven: the tag's own `startDateKey` always wins when set, because work begins
 * before the first milestone is reached — deriving start from the earliest milestone would make
 * expected-progress tracking inaccurate. It only falls back to the earliest sub-task start when
 * the tag has no explicit start. FINISH rolls up from the latest sub-task finish (the last
 * milestone), falling back to the tag's own finish. With no sub-tasks both come from the tag.
 */
export function rollupTagDatesFromSubTasks(
  tag: Doc<"fieldPilotTags">,
  subTasks: { startDateKey?: string | null; finishDateKey?: string | null }[]
): { startDateKey: string | null; finishDateKey: string | null } {
  const starts = subTasks
    .map((s) => parseDateKey(s.startDateKey ?? undefined))
    .filter((ms): ms is number => ms !== null);
  const finishes = subTasks
    .map((s) => parseDateKey(s.finishDateKey ?? undefined))
    .filter((ms): ms is number => ms !== null);

  const startMs = starts.length > 0 ? Math.min(...starts) : null;
  const finishMs = finishes.length > 0 ? Math.max(...finishes) : null;

  return {
    // Explicit tag start wins (work starts before the first milestone); else earliest sub-task start.
    startDateKey: tag.startDateKey ?? (startMs !== null ? msToDateKey(startMs) : null),
    finishDateKey: finishMs !== null ? msToDateKey(finishMs) : tag.finishDateKey ?? null,
  };
}

export interface FieldPilotItemPipelineInfo {
  /** True when the item has reached the final pipeline stage (milestone or completion status). */
  isFinal: boolean;
  nextStatusId: Id<"fieldPilotStatuses"> | null;
  nextStatusName: string | null;
  /** Due date of the next milestone the item must reach (null when ungrouped/finished). */
  nextDueDateKey: string | null;
  /** Due date of the final milestone (or the parent tag's finish) — the target completion date. */
  finalDueDateKey: string | null;
}

/**
 * Per-item pipeline info used by exports: the NEXT status/milestone an item must reach (with its
 * due date) and the FINAL target date. When the item's sub-task defines milestones, those statuses
 * ARE the pipeline; otherwise it falls back to completion-status semantics and the tag's finish date.
 */
export function computeItemPipelineInfo(args: {
  item: Doc<"fieldPilotItems">;
  sortedStatuses: Doc<"fieldPilotStatuses">[];
  statusRank: Map<string, number>;
  completionStatusIds: Set<string>;
  subTaskMilestones?: Doc<"fieldPilotMilestones">[];
  tagFinishDateKey?: string | null;
}): FieldPilotItemPipelineInfo {
  const {
    item,
    sortedStatuses,
    statusRank,
    completionStatusIds,
    subTaskMilestones,
    tagFinishDateKey,
  } = args;

  const itemRank = itemStatusRank(item, statusRank);
  const statusById = new Map(sortedStatuses.map((s) => [String(s._id), s] as const));

  const milestones = (subTaskMilestones ?? [])
    .map((milestone) => ({
      milestone,
      rank: statusRank.get(String(milestone.statusId)) ?? -1,
    }))
    .filter(({ rank }) => rank >= 0)
    .sort((a, b) => a.rank - b.rank);

  if (milestones.length > 0) {
    const finalMilestone = milestones[milestones.length - 1];
    const next = milestones.find(({ rank }) => rank > itemRank) ?? null;
    return {
      isFinal: itemRank >= finalMilestone.rank,
      nextStatusId: next ? next.milestone.statusId : null,
      nextStatusName: next
        ? statusById.get(String(next.milestone.statusId))?.name ?? null
        : null,
      nextDueDateKey: next ? next.milestone.dueDateKey : null,
      finalDueDateKey: finalMilestone.milestone.dueDateKey,
    };
  }

  // No milestone schedule: use completion status as "final" and pipeline order for "next".
  const isFinal = completionStatusIds.has(String(item.statusId));
  const nextStatus = isFinal
    ? null
    : sortedStatuses.find(
        (s) => (statusRank.get(String(s._id)) ?? -1) > itemRank
      ) ?? null;
  return {
    isFinal,
    nextStatusId: nextStatus?._id ?? null,
    nextStatusName: nextStatus?.name ?? null,
    nextDueDateKey: null,
    finalDueDateKey: tagFinishDateKey ?? null,
  };
}
